import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from './supabase'
import { saveReferenceCache, loadReferenceCache, clearReferenceCache } from './offlineStore'

// ── Rule evaluation ────────────────────────────────────────────────────────────

export function evaluateRule(item, field, op, value) {
    if (!item || !field || !op || value === undefined) return false
    let itemVal = (item[field] || '').trim()
    let ruleVal = String(value || '').trim()
    // Case-insensitive comparison for discipline field
    if (field === 'discipline') {
        itemVal = itemVal.toUpperCase()
        ruleVal = ruleVal.toUpperCase()
    }
    switch (op) {
        case 'equals':     return itemVal === ruleVal
        case 'startsWith': return itemVal.startsWith(ruleVal)
        case 'contains':   return itemVal.includes(ruleVal)
        case 'notEquals':  return itemVal !== ruleVal
        default: return false
    }
}

export function evaluateAddonRule(item, addon) {
    if (!addon || !item) return false
    const itemId = String(item.id)
    const excludeIds = (addon.exclude_ids || []).map(String)
    const includeIds = (addon.include_ids || []).map(String)
    // Manually excluded items never belong to this addon
    if (excludeIds.includes(itemId)) return false
    // Manually included items always belong to this addon (regardless of rule)
    if (includeIds.includes(itemId)) return true

    const primary = evaluateRule(item, addon.rule_field, addon.rule_op, addon.rule_value)
    if (!primary) return false

    // Check exclusions (e.g. project_support excludes Site Visit / Site Inspection)
    const exclusions = addon.rule_exclusions || []
    for (const ex of exclusions) {
        if (evaluateRule(item, ex.field, ex.op, ex.value)) return false
    }
    return true
}

// ── Scope item resolution ─────────────────────────────────────────────────────

export function getAddonScopeItemIds(addon, catalogItems, projectType, phase) {
    if (!addon || !catalogItems?.length) return new Set()
    const pt = projectType || ''
    const ph = phase || ''

    // Addon-level restrictions: if addon specifies project_types or phases, enforce them
    const addonPt = addon.project_types || []
    const addonPh = addon.phases || []
    if (addonPt.length > 0 && !addonPt.includes(pt)) return new Set()
    if (addonPh.length > 0 && !addonPh.includes(ph)) return new Set()

    const matched = catalogItems.filter(ci => {
        if (!evaluateAddonRule(ci, addon)) return false
        return true
    })
    const result = new Set(matched.map(ci => ci.id))

    // Remove manually excluded items
    const manualExcludes = (addon.exclude_ids || []).map(String)
    for (const id of manualExcludes) result.delete(id)

    // Add manually included items
    const manualIncludes = (addon.include_ids || []).map(String)
    for (const ci of catalogItems) {
        if (manualIncludes.includes(String(ci.id))) result.add(ci.id)
    }

    return result
}

// ── Add-on gating filter (negative: hide items when toggle is OFF) ────────────

export function applyAddonGatedFilter(scopeItems, addons, addonDefs) {
    if (!addonDefs?.length) return scopeItems || []
    const enabledKeys = new Set((addons || []).filter(a => a.enabled).map(a => a.key))
    const disabledKeys = new Set((addons || []).filter(a => !a.enabled).map(a => a.key))

    return (scopeItems || []).filter(item => {
        const matchingDefs = addonDefs.filter(a => evaluateAddonRule(item, a))
        if (matchingDefs.length === 0) return true // Not tied to any addon → always show
        const hasEnabled = matchingDefs.some(a => enabledKeys.has(a.key))
        if (hasEnabled) return true // At least one matching addon is ON → show
        // All matching addons are disabled → hide
        return false
    })
}

// ── Check if a scope item is tied to ANY add-on (ignores toggle state) ────────

export function isAddonItem(item, addonDefs) {
    if (!item || !addonDefs?.length) return false
    return addonDefs.some(a => evaluateAddonRule(item, a))
}

// ── Orphan detection ─────────────────────────────────────────────────────────

export function getOrphanAddons(estimateAddons, addonDefs) {
    if (!estimateAddons?.length) return []
    const defKeys = new Set((addonDefs || []).map(d => d.key))
    return (estimateAddons || []).filter(a => a.enabled && !defKeys.has(a.key))
}

// ── Label resolution (i18n-aware) ────────────────────────────────────────────

export function getAddonLabel(addon, t) {
    if (!addon) return ''
    if (addon.label_key && t) {
        const translated = t(addon.label_key)
        if (translated && translated !== addon.label_key) return translated
    }
    return addon.label || addon.key || ''
}

// ── Initialize estimate addons from defaults ─────────────────────────────────

export function getDefaultEstimateAddons(addonDefs) {
    if (!addonDefs?.length) return []
    return addonDefs
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
        .map(a => ({
            key: a.key,
            amount: Number(a.default_amount) || 0,
            enabled: Boolean(a.enabled_by_default),
        }))
}

// ── Merge saved estimate addons with current definitions ───────────────────────

export function mergeEstimateAddons(savedAddons, addonDefs) {
    if (!addonDefs?.length) return savedAddons || []
    const defMap = new Map(addonDefs.map(d => [d.key, d]))
    const seen = new Set()
    const result = []

    // 1. Preserve saved order for definitions that still exist
    for (const saved of savedAddons || []) {
        const def = defMap.get(saved.key)
        if (def) {
            result.push({
                key: def.key,
                amount: Number(saved.amount) || Number(def.default_amount) || 0,
                enabled: Boolean(saved.enabled),
            })
            seen.add(saved.key)
        }
    }

    // 2. Append unseen definitions in global sort_order
    const unseenDefs = addonDefs.filter(d => !seen.has(d.key))
    unseenDefs.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
    for (const def of unseenDefs) {
        result.push({
            key: def.key,
            amount: Number(def.default_amount) || 0,
            enabled: Boolean(def.enabled_by_default),
        })
        seen.add(def.key)
    }

    // 3. Append orphan addons (definitions that no longer exist)
    for (const saved of savedAddons || []) {
        if (!defMap.has(saved.key)) {
            result.push({ ...saved, _orphan: true })
        }
    }

    return result
}

// ── Hook: useArchAddons ────────────────────────────────────────────────────────

export function useArchAddons(session) {
    const [items,   setItems]   = useState([])
    const [loading, setLoading] = useState(true)
    const [error,   setError]   = useState(null)
    const userId = session?.user?.id || null
    const loadIdRef = useRef(0)

    const fetchAndApply = useCallback(async (id, silent) => {
        if (!silent) {
            setLoading(true)
        }
        setError(null)
        const cached = await loadReferenceCache('arch_addons', userId).catch(() => null)
        if (cached) {
            setItems(cached.data)
            if (!silent) setLoading(false)
        }
        const { data, error: err } = await supabase
            .from('arch_addons')
            .select('*')
            .order('sort_order', { ascending: true })
            .order('label', { ascending: true })
        if (id !== loadIdRef.current) return
        if (err) {
            console.error('[archAddons] fetch error:', err.message)
            setError(err.message)
            if (!cached) setItems([])
        } else {
            setItems(data || [])
            await saveReferenceCache('arch_addons', data || [], userId).catch(() => {})
        }
        if (!silent) setLoading(false)
    }, [userId])

    const load = useCallback(async () => {
        const id = ++loadIdRef.current
        await fetchAndApply(id, false)
    }, [fetchAndApply])

    const silentReload = useCallback(async () => {
        const id = ++loadIdRef.current
        await fetchAndApply(id, true)
    }, [fetchAndApply])

    useEffect(() => { load() }, [load])

    /** Create a new add-on definition. */
    const createArchAddon = useCallback(async (payload) => {
        if (!payload.key) throw new Error('key is required')
        if (!payload.label) throw new Error('label is required')
        if (!payload.rule_value?.trim()) throw new Error('rule_value is required')
        const trimmed = {
            ...payload,
            key: payload.key.trim().toLowerCase().replace(/\s+/g, '_'),
            label: payload.label.trim(),
            rule_field: payload.rule_field || 'name',
            rule_op: payload.rule_op || 'equals',
            rule_value: payload.rule_value.trim(),
            include_ids: payload.include_ids || [],
            exclude_ids: payload.exclude_ids || [],
            project_types: payload.project_types || [],
            phases: payload.phases || [],
            sort_order: Number(payload.sort_order) || 0,
            default_amount: Number(payload.default_amount) || 0,
            enabled_by_default: Boolean(payload.enabled_by_default),
            created_by: userId,
            updated_by: userId,
        }
        const { data, error: err } = await supabase
            .from('arch_addons')
            .insert(trimmed)
            .select()
            .single()
        if (err) throw new Error(err.message)
        setItems(prev => [...prev, data].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)))
        await clearReferenceCache('arch_addons', userId).catch(() => {})
        return data
    }, [userId])

    /** Update an add-on definition. */
    const updateArchAddon = useCallback(async (id, patch) => {
        const payload = {
            ...patch,
            updated_by: userId,
            updated_at: new Date().toISOString(),
        }
        if (payload.rule_value) payload.rule_value = payload.rule_value.trim()
        if (payload.label) payload.label = payload.label.trim()
        if (payload.key) payload.key = payload.key.trim().toLowerCase().replace(/\s+/g, '_')
        const { data, error: err } = await supabase
            .from('arch_addons')
            .update(payload)
            .eq('id', id)
            .select()
            .single()
        if (err) throw new Error(err.message)
        setItems(prev => prev.map(a => a.id === id ? data : a).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)))
        await clearReferenceCache('arch_addons', userId).catch(() => {})
        return data
    }, [userId])

    /** Delete an add-on definition. System add-ons are protected. */
    const removeArchAddon = useCallback(async (id) => {
        const item = items.find(a => a.id === id)
        if (item?.is_system) throw new Error('System add-ons cannot be deleted.')
        setItems(prev => prev.filter(a => a.id !== id))
        const { error: err } = await supabase
            .from('arch_addons')
            .delete()
            .eq('id', id)
        if (err) {
            console.error('[archAddons] delete error:', err.message)
            throw new Error(err.message)
        }
        await clearReferenceCache('arch_addons', userId).catch(() => {})
    }, [items, userId])

    return { items, loading, error, createArchAddon, updateArchAddon, removeArchAddon, reload: load, silentReload }
}
